import { Doc, Id } from "@/smart-views/client/api";

export const SMART_VIEW_LAYOUT_COLUMNS = 12;
export const SMART_VIEW_HALF_WIDTH = 6;
export const SMART_VIEW_FULL_WIDTH = 12;

export type SmartViewLayoutEntry = Doc<"smartViewVersions">["layout"][number];

export interface SmartViewRenderedLayoutEntry {
  block: Doc<"blocks">;
  // Placeholder blocks render before they have a version; everything else
  // requires a block version to appear in the layout.
  blockVersion: Doc<"blockVersions"> | null;
  layoutEntry: SmartViewLayoutEntry;
}

export interface SmartViewLayoutRow {
  rowIndex: number;
  entries: SmartViewRenderedLayoutEntry[];
}

export type SmartViewBlockDropPlacement = "beforeBlock" | "aboveRow" | "fullLeft" | "fullRight";

export const buildSmartViewLayoutRows = ({
  layout,
  blocks,
  blockVersions,
}: {
  layout: SmartViewLayoutEntry[];
  blocks: Doc<"blocks">[];
  blockVersions: Doc<"blockVersions">[];
}): SmartViewLayoutRow[] => {
  const blockById = new Map<Id<"blocks">, Doc<"blocks">>();
  for (const block of blocks) {
    blockById.set(block._id, block);
  }

  const blockVersionById = new Map<Id<"blockVersions">, Doc<"blockVersions">>();
  for (const blockVersion of blockVersions) {
    blockVersionById.set(blockVersion._id, blockVersion);
  }

  const entriesByRow = new Map<number, SmartViewRenderedLayoutEntry[]>();

  for (const layoutEntry of layout) {
    const block = blockById.get(layoutEntry.blockId);
    if (!block) continue;

    const blockVersionId = layoutEntry.blockVersionId ?? block.currentVersionId;
    const blockVersion = blockVersionId ? blockVersionById.get(blockVersionId) : undefined;

    // Non-placeholder blocks require a version to render.
    if (!blockVersion && !block.isPlaceholder) {
      continue;
    }

    const rowEntries = entriesByRow.get(layoutEntry.row) ?? [];
    rowEntries.push({
      block,
      blockVersion: blockVersion ?? null,
      layoutEntry,
    });
    entriesByRow.set(layoutEntry.row, rowEntries);
  }

  return Array.from(entriesByRow.entries())
    .sort(([rowA], [rowB]) => rowA - rowB)
    .map(([rowIndex, entries]) => ({
      rowIndex,
      entries: entries.sort(
        (entryA, entryB) => (entryA.layoutEntry.col ?? 0) - (entryB.layoutEntry.col ?? 0)
      ),
    }));
};

export const getSmartViewBlockDropPlacement = ({
  sourceBlockId,
  targetBlockId,
  targetHorizontalRatio,
  rows,
}: {
  sourceBlockId: Id<"blocks">;
  targetBlockId: Id<"blocks">;
  targetHorizontalRatio?: number;
  rows: SmartViewLayoutRow[];
}): SmartViewBlockDropPlacement | null => {
  if (sourceBlockId === targetBlockId) {
    return null;
  }

  const source = findEntryForBlock({ rows, blockId: sourceBlockId });
  const target = findEntryForBlock({ rows, blockId: targetBlockId });

  if (!source || !target) {
    return null;
  }

  const targetIsHalfWidth = target.entry.layoutEntry.width <= SMART_VIEW_HALF_WIDTH;
  const targetIsFullWidth = target.entry.layoutEntry.width > SMART_VIEW_HALF_WIDTH;

  if (targetIsHalfWidth) {
    return "beforeBlock";
  }

  if (targetIsFullWidth && targetHorizontalRatio !== undefined) {
    if (targetHorizontalRatio <= 0.2) {
      return "fullLeft";
    }

    if (targetHorizontalRatio >= 0.8) {
      return "fullRight";
    }
  }

  return "aboveRow";
};

export const getSmartViewEmptyHalfSlotTargetBlockId = (
  row: SmartViewLayoutRow
): Id<"blocks"> | null => {
  if (row.entries.length !== 1) {
    return null;
  }

  const [entry] = row.entries;

  if (entry.layoutEntry.width > SMART_VIEW_HALF_WIDTH) {
    return null;
  }

  return entry.block._id;
};

export const canDropBlockOnEmptyHalfSlot = ({
  sourceBlockId,
  targetBlockId,
  rows,
}: {
  sourceBlockId: Id<"blocks">;
  targetBlockId: Id<"blocks">;
  rows: SmartViewLayoutRow[];
}): boolean => {
  if (sourceBlockId === targetBlockId) {
    return false;
  }

  const source = findEntryForBlock({ rows, blockId: sourceBlockId });
  const target = findEntryForBlock({ rows, blockId: targetBlockId });

  if (!source || !target) {
    return false;
  }

  return getSmartViewEmptyHalfSlotTargetBlockId(target.row) === targetBlockId;
};

export const getLayoutRows = (layout: SmartViewLayoutEntry[]): SmartViewLayoutEntry[][] => {
  const rowsByIndex = new Map<number, SmartViewLayoutEntry[]>();

  for (const entry of layout) {
    const entries = rowsByIndex.get(entry.row) ?? [];
    entries.push(entry);
    rowsByIndex.set(entry.row, entries);
  }

  return Array.from(rowsByIndex.entries())
    .sort(([rowA], [rowB]) => rowA - rowB)
    .map(([, entries]) => entries.sort((entryA, entryB) => (entryA.col ?? 0) - (entryB.col ?? 0)));
};

export const normalizeLayoutRows = (rows: SmartViewLayoutEntry[][]): SmartViewLayoutEntry[] => {
  return rows.flatMap((row, rowIndex) => {
    const sortedRow = row
      .filter(entry => entry.width > 0)
      .sort((entryA, entryB) => (entryA.col ?? 0) - (entryB.col ?? 0));

    if (sortedRow.length === 0) {
      return [];
    }

    if (sortedRow.length === 1) {
      const entry = sortedRow[0];
      const width =
        entry.width <= SMART_VIEW_HALF_WIDTH ? SMART_VIEW_HALF_WIDTH : SMART_VIEW_FULL_WIDTH;

      return [{ ...entry, row: rowIndex, col: 0, width, height: entry.height ?? 1 }];
    }

    let nextCol = 0;

    return sortedRow.map(entry => {
      const width = Math.min(entry.width, SMART_VIEW_HALF_WIDTH);
      const nextEntry = {
        ...entry,
        row: rowIndex,
        col: nextCol,
        width,
        height: entry.height ?? 1,
      };

      nextCol += width;

      return nextEntry;
    });
  });
};

export const moveLayoutBlockToFullRow = ({
  layout,
  sourceBlockId,
  targetRow,
}: {
  layout: SmartViewLayoutEntry[];
  sourceBlockId: SmartViewLayoutEntry["blockId"];
  targetRow: number;
}): SmartViewLayoutEntry[] => {
  const rows = getLayoutRows(layout);
  const sourceRowIndex = rows.findIndex(row => row.some(entry => entry.blockId === sourceBlockId));
  const sourceRow = rows[sourceRowIndex];
  const sourceEntry = sourceRow?.find(entry => entry.blockId === sourceBlockId);

  if (!sourceRow || !sourceEntry) {
    return layout;
  }

  const sourceRowWillBeRemoved = sourceRow.length === 1;
  const rowsWithoutSource = rows
    .map((row, rowIndex) => {
      const nextRow = row.filter(entry => entry.blockId !== sourceBlockId);

      if (rowIndex === sourceRowIndex && nextRow.length === 1) {
        return [{ ...nextRow[0], col: 0, width: SMART_VIEW_FULL_WIDTH }];
      }

      return nextRow;
    })
    .filter(row => row.length > 0);
  const boundedTargetRow = clamp(targetRow, 0, rows.length);
  const adjustedTargetRow =
    sourceRowWillBeRemoved && sourceRowIndex < boundedTargetRow
      ? boundedTargetRow - 1
      : boundedTargetRow;

  rowsWithoutSource.splice(clamp(adjustedTargetRow, 0, rowsWithoutSource.length), 0, [
    { ...sourceEntry, col: 0, width: SMART_VIEW_FULL_WIDTH },
  ]);

  return normalizeLayoutRows(rowsWithoutSource);
};

export const moveLayoutBlockToFullRowBeforeBlock = ({
  layout,
  sourceBlockId,
  targetBlockId,
}: {
  layout: SmartViewLayoutEntry[];
  sourceBlockId: SmartViewLayoutEntry["blockId"];
  targetBlockId: SmartViewLayoutEntry["blockId"];
}): SmartViewLayoutEntry[] => {
  if (sourceBlockId === targetBlockId) {
    return layout;
  }

  const rows = getLayoutRows(layout);
  const sourceRowIndex = rows.findIndex(row => row.some(entry => entry.blockId === sourceBlockId));
  const sourceEntry = rows[sourceRowIndex]?.find(entry => entry.blockId === sourceBlockId);

  if (!sourceEntry) {
    return layout;
  }

  const rowsWithoutSource = rows
    .map((row, rowIndex) => {
      const nextRow = row.filter(entry => entry.blockId !== sourceBlockId);

      if (rowIndex === sourceRowIndex && nextRow.length === 1) {
        return [{ ...nextRow[0], col: 0, width: SMART_VIEW_FULL_WIDTH }];
      }

      return nextRow;
    })
    .filter(row => row.length > 0);
  const targetRowIndex = rowsWithoutSource.findIndex(row =>
    row.some(entry => entry.blockId === targetBlockId)
  );

  if (targetRowIndex === -1) {
    return layout;
  }

  rowsWithoutSource.splice(targetRowIndex, 0, [
    { ...sourceEntry, col: 0, width: SMART_VIEW_FULL_WIDTH },
  ]);

  return normalizeLayoutRows(rowsWithoutSource);
};

export const moveLayoutBlockBesideFullBlock = ({
  layout,
  sourceBlockId,
  targetBlockId,
  side,
}: {
  layout: SmartViewLayoutEntry[];
  sourceBlockId: SmartViewLayoutEntry["blockId"];
  targetBlockId: SmartViewLayoutEntry["blockId"];
  side: "left" | "right";
}): SmartViewLayoutEntry[] => {
  if (sourceBlockId === targetBlockId) {
    return layout;
  }

  const rows = getLayoutRows(layout);
  const sourceEntry = layout.find(entry => entry.blockId === sourceBlockId);
  const targetRow = rows.find(row => row.some(entry => entry.blockId === targetBlockId));
  const targetEntry = targetRow?.find(entry => entry.blockId === targetBlockId);

  if (
    !sourceEntry ||
    !targetRow ||
    !targetEntry ||
    targetRow.length !== 1 ||
    targetEntry.width <= SMART_VIEW_HALF_WIDTH
  ) {
    return layout;
  }

  const sourceRowIndex = rows.findIndex(row => row.some(entry => entry.blockId === sourceBlockId));
  const rowsWithoutSource = removeBlockFromRowsAndPromoteRemainder({
    rows,
    sourceBlockId,
    sourceRowIndex,
  });
  const nextTargetRowIndex = rowsWithoutSource.findIndex(row =>
    row.some(entry => entry.blockId === targetBlockId)
  );

  if (nextTargetRowIndex === -1) {
    return layout;
  }

  const sourceHalfEntry = {
    ...sourceEntry,
    col: side === "left" ? 0 : SMART_VIEW_HALF_WIDTH,
    width: SMART_VIEW_HALF_WIDTH,
  };
  const targetHalfEntry = {
    ...targetEntry,
    col: side === "left" ? SMART_VIEW_HALF_WIDTH : 0,
    width: SMART_VIEW_HALF_WIDTH,
  };

  rowsWithoutSource[nextTargetRowIndex] =
    side === "left" ? [sourceHalfEntry, targetHalfEntry] : [targetHalfEntry, sourceHalfEntry];

  return normalizeLayoutRows(rowsWithoutSource);
};

export const moveLayoutBlockBefore = ({
  layout,
  sourceBlockId,
  targetBlockId,
}: {
  layout: SmartViewLayoutEntry[];
  sourceBlockId: SmartViewLayoutEntry["blockId"];
  targetBlockId: SmartViewLayoutEntry["blockId"];
}): SmartViewLayoutEntry[] => {
  if (sourceBlockId === targetBlockId) {
    return layout;
  }

  const rows = getLayoutRows(layout);
  const sourceRowIndex = rows.findIndex(row => row.some(entry => entry.blockId === sourceBlockId));
  const sourceEntry = rows[sourceRowIndex]?.find(entry => entry.blockId === sourceBlockId);
  const targetEntry = layout.find(entry => entry.blockId === targetBlockId);

  if (!sourceEntry || !targetEntry || targetEntry.width > SMART_VIEW_HALF_WIDTH) {
    return layout;
  }

  const rowsWithoutSource = removeBlockFromRowsAndPromoteRemainder({
    rows,
    sourceBlockId,
    sourceRowIndex,
  });
  const targetRowIndex = rowsWithoutSource.findIndex(row =>
    row.some(entry => entry.blockId === targetBlockId)
  );

  if (targetRowIndex === -1) {
    return layout;
  }

  const targetRow = rowsWithoutSource[targetRowIndex];
  const targetIndex = targetRow.findIndex(entry => entry.blockId === targetBlockId);
  const pairedTargetRow = [
    { ...sourceEntry, width: SMART_VIEW_HALF_WIDTH },
    targetRow[targetIndex],
  ];
  const rowsToInsert = [
    targetRow.slice(0, targetIndex),
    pairedTargetRow,
    targetRow.slice(targetIndex + 1),
  ]
    .filter(row => row.length > 0)
    .map(row => {
      if (row !== pairedTargetRow && row.length === 1) {
        return [{ ...row[0], col: 0, width: SMART_VIEW_FULL_WIDTH }];
      }

      return row.map((entry, index) => ({
        ...entry,
        col: index * SMART_VIEW_HALF_WIDTH,
        width: SMART_VIEW_HALF_WIDTH,
      }));
    });

  rowsWithoutSource.splice(targetRowIndex, 1, ...rowsToInsert);

  return normalizeLayoutRows(rowsWithoutSource);
};

export const moveLayoutBlockToEmptyHalfSlot = ({
  layout,
  sourceBlockId,
  targetBlockId,
}: {
  layout: SmartViewLayoutEntry[];
  sourceBlockId: SmartViewLayoutEntry["blockId"];
  targetBlockId: SmartViewLayoutEntry["blockId"];
}): SmartViewLayoutEntry[] => {
  if (sourceBlockId === targetBlockId) {
    return layout;
  }

  const rows = getLayoutRows(layout);
  const sourceRowIndex = rows.findIndex(row => row.some(entry => entry.blockId === sourceBlockId));
  const targetRowIndex = rows.findIndex(row => row.some(entry => entry.blockId === targetBlockId));
  const sourceEntry = rows[sourceRowIndex]?.find(entry => entry.blockId === sourceBlockId);
  const targetRow = rows[targetRowIndex];
  const targetEntry = targetRow?.find(entry => entry.blockId === targetBlockId);

  if (
    !sourceEntry ||
    !targetRow ||
    !targetEntry ||
    targetEntry.width > SMART_VIEW_HALF_WIDTH ||
    targetRow.length !== 1
  ) {
    return layout;
  }

  const rowsWithoutSource = removeBlockFromRowsAndPromoteRemainder({
    rows,
    sourceBlockId,
    sourceRowIndex,
  });
  const nextTargetRow = rowsWithoutSource.find(row =>
    row.some(entry => entry.blockId === targetBlockId)
  );

  if (!nextTargetRow || nextTargetRow.length !== 1) {
    return layout;
  }

  nextTargetRow.push({
    ...sourceEntry,
    col: SMART_VIEW_HALF_WIDTH,
    width: SMART_VIEW_HALF_WIDTH,
  });

  return normalizeLayoutRows(rowsWithoutSource);
};

const findEntryForBlock = ({
  rows,
  blockId,
}: {
  rows: SmartViewLayoutRow[];
  blockId: Id<"blocks">;
}): { row: SmartViewLayoutRow; entry: SmartViewRenderedLayoutEntry } | null => {
  for (const row of rows) {
    const entry = row.entries.find(candidate => candidate.block._id === blockId);

    if (entry) {
      return { row, entry };
    }
  }

  return null;
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(value, max));
};

const removeBlockFromRowsAndPromoteRemainder = ({
  rows,
  sourceBlockId,
  sourceRowIndex,
}: {
  rows: SmartViewLayoutEntry[][];
  sourceBlockId: SmartViewLayoutEntry["blockId"];
  sourceRowIndex: number;
}): SmartViewLayoutEntry[][] => {
  return rows
    .map((row, rowIndex) => {
      const nextRow = row.filter(entry => entry.blockId !== sourceBlockId);

      if (rowIndex === sourceRowIndex && nextRow.length === 1) {
        return [{ ...nextRow[0], col: 0, width: SMART_VIEW_FULL_WIDTH }];
      }

      return nextRow;
    })
    .filter(row => row.length > 0);
};
